<template>
  <Modal
    :title="`Send Pay Link — Invoice #${invoice.invoice_id}`"
    :saving="saving"
    saving-text="Sending..."
    :submit-btn-text="hasActiveLink ? 'Resend Link' : 'Send Link'"
    submit-btn-class="btn btn-sm btn-primary ml-auto"
    data-cy="send-pay-link-modal"
    @submit="handleSend"
    @hide-modal="$emit('hide-modal')"
  >
    <div class="form-group mb-0">
      <p class="mb-3 text-gray-700">
        Text the customer a secure link to pay this invoice. Leave the number
        as-is or edit it before sending.
      </p>
      <TextField
        v-model="phone"
        name="phone"
        label-text="Mobile number"
        placeholder="Customer's mobile number"
        data-cy="pay-link-phone"
      />
      <div v-if="hasActiveLink" class="notice-message mt-2">
        <i class="far fa-circle-info mr-1"></i>
        <span>
          A link is already active. Resending sends a new link and the previous
          one stops working.
        </span>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useStore } from "vuex";

import Modal from "@/components/Modal.vue";
import TextField from "@/components/TextField.vue";
import type { Invoice } from "@/typings/invoice";

const props = defineProps<{ invoice: Invoice }>();
const emit = defineEmits<{
  (e: "hide-modal"): void;
  (e: "sent"): void;
}>();

const store = useStore();
const saving = ref(false);
const phone = ref<string>(props.invoice.customer?.phone ?? "");

// The server includes links.payby to send and revoke_pay_link while a link is
// live; the latter drives the "Resend" label and the already-active notice.
const sendUrl = computed<string | undefined>(() => props.invoice.links?.payby);
const hasActiveLink = computed<boolean>(() =>
  Boolean(props.invoice.links?.revoke_pay_link)
);

// On failure the global response interceptor (vuex/interceptors.js) already
// shows the endpoint's error message, so the catch here only stops the success
// path (no "sent"/"revoked", modal stays open) — it must not toast again or the
// user sees the same error twice.
async function handleSend(): Promise<void> {
  if (!sendUrl.value || saving.value) return;
  saving.value = true;
  try {
    await store.dispatch("sendPayLink", {
      url: sendUrl.value,
      phoneNumber: phone.value?.trim() || undefined
    });
    (window as any).notify.success("Pay link sent.");
    emit("sent");
    emit("hide-modal");
  } catch {
    // error surfaced globally; keep the modal open
  } finally {
    saving.value = false;
  }
}
</script>
